// Port of TrailData/Store/SpatialIndex.swift: a uniform latitude/longitude grid of item indices and a chunked
// polyline geometry for fast point-to-line distances.

import { boundsOf, distance, distanceToSegment, isValidCoordinate } from '../core/geo';
import type { GeoBounds, GeoCoordinate, GeoPolyline } from '../core/models';

interface CellRange {
  minRow: number;
  maxRow: number;
  minColumn: number;
  maxColumn: number;
  count: number;
}

/** Column offset of the numeric cell key: keys stay unique while |column| < 2^21 (cell size above ~0.0001°). */
const columnSpan = 4_194_304; // 2^22

/**
 * Uniform latitude/longitude grid mapping cells to item indices (points or bounding boxes).
 *
 * Items whose bounds would cover more than `maxCellsPerItem` cells (e.g. a country-sized polygon) are kept in a small
 * "oversized" list returned for every query instead of being written into thousands of cells.
 */
export class GridIndex {
  private readonly cells = new Map<number, number[]>();
  private readonly oversized: number[] = [];

  constructor(
    readonly cellDegrees: number,
    readonly maxCellsPerItem = 4_096,
  ) {
    if (!(cellDegrees > 0)) throw new RangeError('cell size must be positive');
  }

  /** Number of populated cells. */
  get populatedCellCount(): number {
    return this.cells.size;
  }

  /** Indexes a point item. Invalid coordinates are ignored (they can never match a query). */
  insertPoint(item: number, coordinate: GeoCoordinate): void {
    if (!isValidCoordinate(coordinate)) return;
    this.append(this.key(this.row(coordinate.latitude), this.column(coordinate.longitude)), item);
  }

  /** Indexes an item by its bounding box. Invalid bounds are ignored. */
  insertBounds(item: number, bounds: GeoBounds): void {
    const range = this.cellRange(bounds);
    if (!range) return;
    if (range.count > this.maxCellsPerItem) {
      this.oversized.push(item);
      return;
    }
    for (let r = range.minRow; r <= range.maxRow; r++) {
      for (let c = range.minColumn; c <= range.maxColumn; c++) this.append(this.key(r, c), item);
    }
  }

  /** Indices of items whose cells intersect `bounds` (a superset of the exact answer), ascending and unique. */
  candidates(bounds: GeoBounds): number[] {
    const range = this.cellRange(bounds);
    if (!range) return [];
    const result: number[] = this.oversized.slice();
    if (range.count > this.cells.size) {
      // Huge query relative to the populated grid: scanning populated cells is cheaper.
      for (const [key, items] of this.cells) {
        const r = Math.round(key / columnSpan);
        const c = key - r * columnSpan;
        if (r >= range.minRow && r <= range.maxRow && c >= range.minColumn && c <= range.maxColumn) {
          for (const item of items) result.push(item);
        }
      }
    } else {
      for (let r = range.minRow; r <= range.maxRow; r++) {
        for (let c = range.minColumn; c <= range.maxColumn; c++) {
          const items = this.cells.get(this.key(r, c));
          if (items) for (const item of items) result.push(item);
        }
      }
    }
    result.sort((a, b) => a - b);
    const unique: number[] = [];
    for (const item of result) if (unique[unique.length - 1] !== item) unique.push(item);
    return unique;
  }

  // MARK: Cell arithmetic

  private append(key: number, item: number): void {
    const items = this.cells.get(key);
    if (items) items.push(item);
    else this.cells.set(key, [item]);
  }

  private key(row: number, column: number): number {
    return row * columnSpan + column;
  }

  private cellRange(b: GeoBounds): CellRange | undefined {
    if (
      !Number.isFinite(b.minLatitude) ||
      !Number.isFinite(b.maxLatitude) ||
      !Number.isFinite(b.minLongitude) ||
      !Number.isFinite(b.maxLongitude) ||
      b.minLatitude > b.maxLatitude ||
      b.minLongitude > b.maxLongitude
    ) {
      return undefined;
    }
    const minRow = this.row(b.minLatitude);
    const maxRow = this.row(b.maxLatitude);
    const minColumn = this.column(b.minLongitude);
    const maxColumn = this.column(b.maxLongitude);
    return { minRow, maxRow, minColumn, maxColumn, count: (maxRow - minRow + 1) * (maxColumn - minColumn + 1) };
  }

  private row(latitude: number): number {
    return Math.floor(Math.min(90, Math.max(-90, latitude)) / this.cellDegrees);
  }

  private column(longitude: number): number {
    return Math.floor(Math.min(180, Math.max(-180, longitude)) / this.cellDegrees);
  }
}

interface Chunk {
  /** First vertex; consecutive chunks share their boundary vertex so no segment is lost. */
  start: number;
  /** Last vertex (inclusive). */
  end: number;
  bounds: GeoBounds;
}

/**
 * Precomputed polyline geometry for fast point-to-line distances: vertices are grouped into chunks with bounding boxes
 * so whole chunks can be skipped when they cannot beat the current best distance.
 */
export class PolylineDistanceIndex {
  static readonly chunkSize = 32;

  readonly coordinates: GeoCoordinate[];
  readonly bounds: GeoBounds | undefined;
  private readonly chunks: Chunk[] = [];

  constructor(polyline: GeoPolyline) {
    const coordinates: GeoCoordinate[] = [];
    for (const p of polyline.points) if (isValidCoordinate(p.coordinate)) coordinates.push(p.coordinate);
    this.coordinates = coordinates;
    this.bounds = boundsOf(coordinates);
    if (coordinates.length >= 2) {
      let start = 0;
      while (start < coordinates.length - 1) {
        const end = Math.min(start + PolylineDistanceIndex.chunkSize, coordinates.length - 1);
        const bounds = boundsOf(coordinates.slice(start, end + 1));
        if (bounds) this.chunks.push({ start, end, bounds });
        start = end;
      }
    }
  }

  /** A lower bound (metres) of the distance from `p` to any vertex or segment of the polyline. */
  lowerBoundDistance(p: GeoCoordinate): number {
    return this.bounds ? PolylineDistanceIndex.lowerBoundDistance(p, this.bounds) : Infinity;
  }

  /**
   * Minimum distance (metres) from `p` to the polyline, or `Infinity` when the polyline is empty or no part of it is
   * closer than `limit` (chunks that cannot beat `limit` are skipped).
   */
  distance(p: GeoCoordinate, limit = Infinity): number {
    const first = this.coordinates[0];
    if (!first) return Infinity;
    if (this.coordinates.length === 1) {
      const d = distance(p, first);
      return d <= limit ? d : Infinity;
    }
    let best = Infinity;
    for (const chunk of this.chunks) {
      const bound = PolylineDistanceIndex.lowerBoundDistance(p, chunk.bounds);
      if (bound > Math.min(best, limit)) continue;
      for (let i = chunk.start; i < chunk.end; i++) {
        const d = distanceToSegment(p, this.coordinates[i], this.coordinates[i + 1]);
        if (d < best) best = d;
      }
    }
    return best <= limit ? best : Infinity;
  }

  /**
   * Conservative lower bound of the distance from `p` to a latitude/longitude box: the distance to the clamped point,
   * reduced by 1 % + 1 m to absorb the (tiny) difference between the clamped point and the true nearest point.
   */
  static lowerBoundDistance(p: GeoCoordinate, b: GeoBounds): number {
    const lat = Math.min(Math.max(p.latitude, b.minLatitude), b.maxLatitude);
    const lon = Math.min(Math.max(p.longitude, b.minLongitude), b.maxLongitude);
    if (lat === p.latitude && lon === p.longitude) return 0;
    return Math.max(0, distance(p, { latitude: lat, longitude: lon }) * 0.99 - 1);
  }
}

/** Degenerate bounds containing a single coordinate (Swift `GeoBounds(point:)`). */
export function pointBounds(c: GeoCoordinate): GeoBounds {
  return { minLatitude: c.latitude, minLongitude: c.longitude, maxLatitude: c.latitude, maxLongitude: c.longitude };
}
